import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Calendar, ChevronRight } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useAddIftaMutation } from "../../redux/services/IftaComplianceService";
import { sweetAlert } from "../../utils";
import { responseError } from "../../helpers/responseHandler";
import { useNavigate } from "react-router-dom";

const QUARTERS = [
  { id: 1, label: "1st Quarter (Jan - Mar)" },
  { id: 2, label: "2nd Quarter (Apr - Jun)" },
  { id: 3, label: "3rd Quarter (Jul - Sep)" },
  { id: 4, label: "4th Quarter (Oct - Dec)" },
];

const IftaModal = ({ show, onClose, tractors }) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const [addRequest, { isLoading: addLoading }] = useAddIftaMutation();
  const navigate = useNavigate();

  const [truckId, setTruckId] = useState("TRK-001");
  const [year, setYear] = useState("2026");
  const [quarter, setQuarter] = useState(1);
  const selectedQuarter = watch("quarter");
  const selectedYear = watch("year");
  const dateRange =
    QUARTERS.find((q) => q.id === Number(selectedQuarter))?.label ||
    "1st Quarter (Jan - Mar)";

  const onSubmit = async (data) => {
    if (data.vehicle === "all") {
      data.tractor_trailer_id = null;
      data.type = "all";
      data.vehicle = "";
    } else {
      const [tractorId, vehicleName] = data.vehicle.split("_");
      data.tractor_trailer_id = Number(tractorId);
      data.vehicle = vehicleName;
      data.type = "single";
    }

    await addRequest(data)
      .unwrap()
      .then((res) => {
        if (res?.status) {
          sweetAlert(res?.message, "success");

          const createdId = res?.response?.data?.id;

          onClose();
          navigate(`/ifta-compliance-view/${createdId}`);
        }
      })
      .catch(responseError);
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Modal show={show} onHide={handleCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>Start New IFTA Filing</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)} id="ifta-form">
          {/* Select Vehicle */}
          <Form.Group className="mb-3">
            <Form.Label>Select Vehicle</Form.Label>
            <Controller
              name="vehicle"
              control={control}
              defaultValue={"all"}
              rules={{ required: "Vehicle is required" }}
              render={({ field }) => (
                <Form.Select {...field}>
                  <option value="">Please Select Option</option>
                  <option value="all">All Fleet</option>
                  {tractors?.map((tractor) => (
                    <option
                      key={tractor?.id}
                      value={`${tractor?.id}_${tractor?.make}-${tractor?.model}`}
                    >
                      {tractor?.make}-{tractor?.model}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            {errors.vehicle && (
              <small className="text-danger">{errors.vehicle.message}</small>
            )}
          </Form.Group>

          <div className="row g-3">
            {/* Year */}
            <div className="col">
              <Form.Group>
                <Form.Label>Filing Year</Form.Label>
                <Controller
                  name="year"
                  control={control}
                  rules={{ required: "Year is required" }}
                  render={({ field }) => (
                    <Form.Select {...field}>
                      <option value="">Please Select Option</option>
                      <option value="2024">2024</option>
                      <option value="2025">2025</option>
                      <option value="2026">2026</option>
                      <option value="2027">2027</option>
                      <option value="2028">2028</option>
                      <option value="2029">2029</option>
                      <option value="2030">2030</option>
                    </Form.Select>
                  )}
                />
                {errors.year && (
                  <small className="text-danger">{errors.year.message}</small>
                )}
              </Form.Group>
            </div>

            {/* Quarter */}
            <div className="col">
              <Form.Group>
                <Form.Label>Quarter</Form.Label>
                <Controller
                  name="quarter"
                  control={control}
                  rules={{ required: "Quarter is required" }}
                  render={({ field }) => (
                    <Form.Select
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value="">Please Select Option</option>
                      {QUARTERS.map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                {errors.quarter && (
                  <small className="text-danger">
                    {errors.quarter.message}
                  </small>
                )}
              </Form.Group>
            </div>
          </div>

          {/* Filing Period */}
          <div className="mt-3 p-2 border d-flex align-items-start gap-2 bg-light">
            <Calendar size={20} className="text-primary mt-1" />
            <div>
              <p className="mb-0 small text-uppercase text-primary fw-bold">
                Filing Period
              </p>
              <p className="mb-0">
                {selectedYear ? selectedYear : year} - {dateRange}
              </p>
              <p className="text-muted small pt-2">
                This Calculator aims to provide estimations based on standard
                IFTA-101 logic. Rates used are illustrative 2025 Q4 samples.
                Official returns should verify rates with IFTA Inc.
              </p>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" type="button" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" form="ifta-form">
          Create Filing <ChevronRight size={16} className="ms-1" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IftaModal;
